import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  Radar,
  Search,
  Stethoscope,
  PenSquare,
  GitBranch,
  Activity,
  Users2,
  LogOut,
  Home,
  ChevronRight,
  KeyRound,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Overview", icon: Home, exact: true, code: "00" },
  { to: "/scan", label: "Scan", icon: Search, code: "01" },
  { to: "/diagnose", label: "Diagnose", icon: Stethoscope, code: "02" },
  { to: "/prescribe", label: "Prescribe", icon: PenSquare, code: "03" },
  { to: "/transform", label: "Transform", icon: GitBranch, code: "04" },
  { to: "/sustain", label: "Sustain", icon: Activity, code: "05" },
  { to: "/users", label: "Users", icon: Users2, code: "U" },
];

export default function AppShell() {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const current = NAV.find(
    (n) => (n.exact ? loc.pathname === "/" : loc.pathname.startsWith(n.to))
  ) || NAV[0];

  return (
    <div className="min-h-screen flex bg-[color:var(--bg-base)] text-white">
      {/* Sidebar */}
      <aside
        data-testid="sidebar"
        className="w-64 shrink-0 border-r border-[color:var(--border)] flex flex-col relative"
      >
        <div className="p-5 border-b border-[color:var(--border)] flex items-center gap-3">
          <div className="w-8 h-8 border border-[color:var(--accent)] flex items-center justify-center text-[color:var(--accent)]">
            <Radar size={16} strokeWidth={1.5} />
          </div>
          <div>
            <div className="mono text-[10px] tracking-[0.28em] text-[color:var(--text-3)]">
              IVOROVI
            </div>
            <div className="text-sm font-medium tracking-tight">Revora</div>
          </div>
        </div>

        <nav className="flex-1 p-3 space-y-1">
          <div className="eyebrow px-2 py-2">/ revora cycle</div>
          {NAV.filter((n) => n.to !== "/users").map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.exact}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `group flex items-center gap-3 px-3 py-2.5 border-l-2 transition-colors duration-150 ${
                    isActive
                      ? "border-[color:var(--accent)] bg-[color:var(--bg-1)] text-white"
                      : "border-transparent text-[color:var(--text-2)] hover:text-white hover:bg-[color:var(--bg-1)]"
                  }`
                }
              >
                <span className="mono text-[10px] text-[color:var(--text-3)] w-6">
                  {n.code}
                </span>
                <Icon size={16} strokeWidth={1.5} />
                <span className="text-sm">{n.label}</span>
                <ChevronRight
                  size={12}
                  className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity duration-150"
                />
              </NavLink>
            );
          })}

          <div className="eyebrow px-2 pt-6 pb-2">/ workspace</div>
          <NavLink
            to="/users"
            data-testid="nav-users"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 border-l-2 transition-colors duration-150 ${
                isActive
                  ? "border-[color:var(--accent)] bg-[color:var(--bg-1)] text-white"
                  : "border-transparent text-[color:var(--text-2)] hover:text-white hover:bg-[color:var(--bg-1)]"
              }`
            }
          >
            <span className="mono text-[10px] text-[color:var(--text-3)] w-6">U</span>
            <Users2 size={16} strokeWidth={1.5} />
            <span className="text-sm">Users</span>
          </NavLink>
          <NavLink
            to="/settings"
            data-testid="nav-settings"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 border-l-2 transition-colors duration-150 ${
                isActive
                  ? "border-[color:var(--accent)] bg-[color:var(--bg-1)] text-white"
                  : "border-transparent text-[color:var(--text-2)] hover:text-white hover:bg-[color:var(--bg-1)]"
              }`
            }
          >
            <span className="mono text-[10px] text-[color:var(--text-3)] w-6">S</span>
            <KeyRound size={16} strokeWidth={1.5} />
            <span className="text-sm">Settings</span>
          </NavLink>
        </nav>

        <div className="p-3 border-t border-[color:var(--border)]">
          <div className="p-3 bg-[color:var(--bg-1)] border border-[color:var(--border)]">
            <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--text-3)]">
              signed in
            </div>
            <div className="text-sm font-medium truncate mt-1">
              {user?.name}
            </div>
            <div className="mono text-[10px] text-[color:var(--text-2)] truncate">
              {user?.email}
            </div>
            <div className="mt-2 flex items-center justify-between">
              <span className="mono text-[9px] uppercase tracking-widest text-[color:var(--accent)] border border-[color:var(--accent)]/40 px-1.5 py-0.5">
                {user?.role}
              </span>
              <button
                data-testid="logout-btn"
                onClick={logout}
                className="text-[color:var(--text-2)] hover:text-[color:var(--critical)] transition-colors duration-150"
                title="Sign out"
              >
                <LogOut size={14} strokeWidth={1.5} />
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 flex flex-col">
        <header
          data-testid="topbar"
          className="h-14 border-b border-[color:var(--border)] px-6 flex items-center justify-between bg-[color:var(--bg-base)] sticky top-0 z-30"
        >
          <div className="flex items-center gap-2 mono text-xs text-[color:var(--text-2)]">
            <span className="text-[color:var(--text-3)]">revora</span>
            <span className="text-[color:var(--text-3)]">/</span>
            <span className="text-white uppercase tracking-widest">
              {current.label}
            </span>
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden md:flex items-center gap-2 mono text-[10px] uppercase tracking-widest text-[color:var(--text-3)]">
              <span className="w-1.5 h-1.5 bg-[color:var(--success)] pulse-dot rounded-full" />
              engine online · claude-sonnet-4.5
            </div>
          </div>
        </header>

        <div className="flex-1 min-w-0 min-h-0">
          <Outlet />
        </div>

        <footer
          data-testid="app-footer"
          className="border-t border-[color:var(--border)] px-6 py-3 flex items-center justify-between gap-4 flex-wrap mono text-[10px] uppercase tracking-widest text-[color:var(--text-3)] bg-[color:var(--bg-base)]"
        >
          <span data-testid="app-copyright">© 2026 IVOROVI. All rights reserved.</span>
          <a
            href="https://www.ivorovi.com"
            target="_blank"
            rel="noreferrer"
            data-testid="app-website"
            className="text-[color:var(--text-2)] hover:text-[color:var(--accent)] transition-colors duration-150"
          >
            www.ivorovi.com
          </a>
        </footer>
      </main>
    </div>
  );
}
